import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Badge,
  Collapse,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Popover,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { formatDateTime, parseUtcDate, getWeekStart, getWeekLabel } from '../utils/dateHelpers';
import {
  ArrowBack as ArrowBackIcon,
  Message as MessageIcon,
  Cancel as CancelIcon,
  SwapHoriz as ReassignIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MarkEmailUnread as UnreadIcon,
  RestartAlt as ResetIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Repeat as RepeatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as SettlementIcon,
  PersonRemove as UnassignIcon,
  PersonAdd as AssignIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import DriverMessagingModal from './DriverMessagingModal';
import EditRideModal from './EditRideModal';
import { dashboardAPI, driversAPI, ridesAPI, paymentAPI } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { safeInvoke } from '../utils/signalRHelper';
import { getRideStatus, getRideStatusColor, getRideStatusStyle, getDriverStatusColor } from '../utils/Status';
import { useNavigate, useLocation } from 'react-router-dom';
import PaymentForm from './PaymentForm';

const MetricListView = ({
  isAdmin = false,
  metricType,
  data: initialData,
  onItemClick,
  onBackToDashboard,
  driversWithUnread = new Map(),
  onMarkMessagesRead,
  onMessagingModalClose,
  onNavigateToRideHistory,
  openMessagingModal,
  openMessagingDriverId
}) => {
  const { signalRConnection, connectSignalR } = useAuth();
  const { showAlert, showToast } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();

  // State declarations must come before callbacks that use them
  const [data, setData] = useState(initialData || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMessaging, setShowMessaging] = useState(false);
  const [messagingDriverId, setMessagingDriverId] = useState(null);
  const [messagingDriverName, setMessagingDriverName] = useState('');
  const [messagingRideContext, setMessagingRideContext] = useState(null); // { rideId, customerName, customerPhone }
  const [driverStatuses, setDriverStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingPriceRideId, setEditingPriceRideId] = useState(null);
  const [editedPrice, setEditedPrice] = useState('');
  const [editedDriverComp, setEditedDriverComp] = useState('');

  // Save scroll position before navigating away
  const saveScrollPosition = useCallback(() => {
    sessionStorage.setItem('metricListView_scrollPosition', window.scrollY.toString());
    sessionStorage.setItem('metricListView_searchQuery', searchQuery);
    sessionStorage.setItem('metricListView_metricType', metricType);
  }, [searchQuery, metricType]);

  // Restore scroll position after returning
  const restoreScrollPosition = useCallback(() => {
    const savedMetricType = sessionStorage.getItem('metricListView_metricType');
    // Only restore if we're on the same metric type
    if (savedMetricType === metricType) {
      const savedPosition = sessionStorage.getItem('metricListView_scrollPosition');
      const savedSearch = sessionStorage.getItem('metricListView_searchQuery');
      if (savedSearch) {
        setSearchQuery(savedSearch);
      }
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedPosition, 10), behavior: 'instant' });
        }, 100);
      }
    }
  }, [metricType]);

  // Wrapper for onItemClick that saves state before navigating
  const handleItemClick = useCallback((type, id) => {
    saveScrollPosition();
    if (onItemClick) {
      onItemClick(type, id);
    }
  }, [onItemClick, saveScrollPosition]);

  // Edit ride modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRide, setEditingRide] = useState(null);

  // Unsettled drivers state
  const [unsettledRidesCounts, setUnsettledRidesCounts] = useState({}); // {driverId: count}
  const [expandedDrivers, setExpandedDrivers] = useState(new Set());
  const [unsettledRidesMap, setUnsettledRidesMap] = useState({}); // {driverId: rides[]}
  const [loadingUnsettledRides, setLoadingUnsettledRides] = useState({});
  const [settlingDrivers, setSettlingDrivers] = useState({});
  const [settlingRides, setSettlingRides] = useState({});

  // Week grouping state for ride lists
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());

  // Assign/Reassign driver menu state
  const [assignMenuAnchorEl, setAssignMenuAnchorEl] = useState(null);
  const [assignMenuRideId, setAssignMenuRideId] = useState(null);

  // Paid status toggle state
  const [paidStatusModalOpen, setPaidStatusModalOpen] = useState(false);
  const [paidStatusRide, setPaidStatusRide] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  // Newly-collected card-on-file token (ccof:...) when a dispatcher adds a card at mark-paid time
  const [collectedCardToken, setCollectedCardToken] = useState(null);
  const [settingPaidStatus, setSettingPaidStatus] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Restore scroll position on mount
  useEffect(() => {
    restoreScrollPosition();
    // Clear the saved position after restoring
    return () => {
      // Don't clear on unmount - we want it to persist for back navigation
    };
  }, [restoreScrollPosition]);

  // Fetch the latest data for the current metricType from the server.
  // Used for initial load and to refresh the list after any mutation (edit, complete, etc.)
  // so the list never shows stale ride data.
  const fetchData = useCallback(async () => {
    if (!metricType) return [];

    let fetchedData = [];
    switch (metricType) {
      case 'assignedRides':
        fetchedData = await dashboardAPI.getAssignedRides();
        break;
      case 'ridesInProgress':
        fetchedData = await dashboardAPI.getRidesInProgress();
        break;
      case 'openRides':
        fetchedData = await dashboardAPI.getOpenRides();
        break;
      case 'completedUnreviewedRides':
        fetchedData = await dashboardAPI.getCompletedUnreviewedRides();
        break;
      case 'recurringRidesThisWeek':
        fetchedData = await dashboardAPI.getRecurringRidesThisWeek();
        break;
      case 'todaysRecurringRides':
        fetchedData = await dashboardAPI.getTodaysRecurringRides();
        break;
      case 'todaysRides':
        fetchedData = await dashboardAPI.getTodaysRides();
        break;
      case 'activeDrivers':
        fetchedData = await dashboardAPI.getActiveDrivers();
        break;
      case 'offlineDrivers':
        fetchedData = await dashboardAPI.getOfflineDrivers();
        break;
      case 'driversOnJob':
        fetchedData = await dashboardAPI.getDriversOnJob();
        break;
      case 'unsettledDrivers':
        fetchedData = await dashboardAPI.getUnsettledDrivers();
        break;
      case 'futureRides':
        fetchedData = await dashboardAPI.getFutureRides();
        break;
      default:
        console.warn('Unknown metric type:', metricType);
    }

    const result = fetchedData || [];
    setData(result);
    return result;
  }, [metricType]);

  // Load data based on metricType when component mounts or when initialData is empty
  useEffect(() => {
    const loadData = async () => {
      const hasInitialData = initialData && initialData.length > 0;

      // Paint initialData immediately for a fast render (e.g. when coming from the dashboard tile)
      if (hasInitialData) {
        setData(initialData);
      }

      // Always refetch fresh data from the server when we have a metricType.
      // initialData can be stale (it's captured once when the metric tile is clicked and
      // isn't updated after edits), so returning to the list must pull the latest values.
      if (!metricType) return;

      if (!hasInitialData) setLoading(true);
      try {
        await fetchData();
      } catch (error) {
        console.error('Failed to load data for metric:', metricType, error);
        if (!hasInitialData) setData([]);
      } finally {
        if (!hasInitialData) setLoading(false);
      }
    };

    loadData();
  }, [metricType, initialData, fetchData]);

  // Fetch driver statuses when component mounts or data changes
  useEffect(() => {
    const fetchDriverStatuses = async () => {
      if (Array.isArray(data) && data.length > 0 && (metricType?.includes('Drivers') || metricType?.includes('drivers'))) {
        const statusPromises = data.map(async (driver) => {
          try {
            const status = await driversAPI.getDriverStatus(driver.id);
            return { id: driver.id, status };
          } catch (error) {
            console.error(`Failed to fetch status for driver ${driver.id}:`, error);
            return { id: driver.id, status: 'unknown' };
          }
        });

        const results = await Promise.all(statusPromises);
        const statusMap = {};
        results.forEach(({ id, status }) => {
          statusMap[id] = status;
        });

        setDriverStatuses(statusMap);
      }
    };

    fetchDriverStatuses();
  }, [data, metricType]);

  // Fetch unsettled rides counts for unsettledDrivers view
  useEffect(() => {
    const fetchUnsettledRidesCounts = async () => {
      if (metricType === 'unsettledDrivers' && Array.isArray(data) && data.length > 0) {
        const countPromises = data.map(async (driver) => {
          try {
            const rides = await dashboardAPI.getUnsettledRidesByDriver(driver.id);
            return { id: driver.id, count: rides?.length || 0 };
          } catch (error) {
            console.error(`Failed to fetch unsettled rides for driver ${driver.id}:`, error);
            return { id: driver.id, count: 0 };
          }
        });

        const results = await Promise.all(countPromises);
        const countsMap = {};
        results.forEach(({ id, count }) => {
          countsMap[id] = count;
        });

        setUnsettledRidesCounts(countsMap);
      }
    };

    fetchUnsettledRidesCounts();
  }, [data, metricType]);

  // Toggle driver expansion and load their unsettled rides
  const toggleDriverExpand = async (driverId) => {
    const isExpanded = expandedDrivers.has(driverId);

    if (isExpanded) {
      setExpandedDrivers(prev => {
        const newSet = new Set(prev);
        newSet.delete(driverId);
        return newSet;
      });
    } else {
      setExpandedDrivers(prev => new Set([...prev, driverId]));

      if (!unsettledRidesMap[driverId]) {
        setLoadingUnsettledRides(prev => ({ ...prev, [driverId]: true }));
        try {
          const rides = await dashboardAPI.getUnsettledRidesByDriver(driverId);
          setUnsettledRidesMap(prev => ({ ...prev, [driverId]: rides || [] }));
        } catch (error) {
          console.error('Error loading unsettled rides:', error);
        } finally {
          setLoadingUnsettledRides(prev => ({ ...prev, [driverId]: false }));
        }
      }
    }
  };

  const calculateUnsettledAmount = (ride) => {
    const cost = ride.cost || 0;
    const driversComp = ride.driversCompensation || 0;

    // If prepaid (CC on file), company owes driver their compensation
    // If not prepaid (cash/zelle/driver collects), driver owes company the profit
    const isPrepaid = ride.paid || ride.paymentTokenId || ride.paymentType === 'dispatcherCC';

    if (isPrepaid) {
      return driversComp; // Company owes driver
    } else {
      return -(cost - driversComp); // Driver owes company
    }
  };

  // Settle all rides for a driver
  const handleSettleUpDriver = async (driverId) => {
    setSettlingDrivers(prev => ({ ...prev, [driverId]: true }));
    try {
      await dashboardAPI.settleUpDriver(driverId);
      showToast('Driver settled up successfully', 'success');
      // Remove driver from list
      setData(prev => prev.filter(d => d.id !== driverId));
      // Clear their data
      setUnsettledRidesMap(prev => {
        const newMap = { ...prev };
        delete newMap[driverId];
        return newMap;
      });
      setUnsettledRidesCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[driverId];
        return newCounts;
      });
    } catch (error) {
      console.error('Error settling driver:', error);
      showAlert('Error', 'Failed to settle up driver. Please try again.', [{ text: 'OK' }], 'error');
    } finally {
      setSettlingDrivers(prev => ({ ...prev, [driverId]: false }));
    }
  };

  // Settle a single ride
  const handleSettleRide = async (driverId, rideId) => {
    setSettlingRides(prev => ({ ...prev, [rideId]: true }));
    try {
      await dashboardAPI.settleRide(rideId);
      showToast('Ride settled successfully', 'success');
      // Remove ride from driver's list
      setUnsettledRidesMap(prev => ({
        ...prev,
        [driverId]: (prev[driverId] || []).filter(r => r.rideId !== rideId)
      }));
      // Update count
      setUnsettledRidesCounts(prev => ({
        ...prev,
        [driverId]: Math.max(0, (prev[driverId] || 1) - 1)
      }));
      // If no more rides, remove driver from list
      const remainingRides = (unsettledRidesMap[driverId] || []).filter(r => r.rideId !== rideId);
      if (remainingRides.length === 0) {
        setData(prev => prev.filter(d => d.id !== driverId));
      }
    } catch (error) {
      console.error('Error settling ride:', error);
      showAlert('Error', 'Failed to settle ride. Please try again.', [{ text: 'OK' }], 'error');
    } finally {
      setSettlingRides(prev => ({ ...prev, [rideId]: false }));
    }
  };

  // Dispatcher complete / uncomplete
  const [dispatcherCompletingId, setDispatcherCompletingId] = useState(null);
  const [dispatcherUncompletingId, setDispatcherUncompletingId] = useState(null);
  // Send Receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptRideId, setReceiptRideId] = useState(null);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [receiptSending, setReceiptSending] = useState(false);

  const handleDispatcherComplete = (ride) => {
    const isCC = ride.paymentType === 'dispatcherCC' && ride.paymentTokenId && !ride.paid;
    const total = ((ride.cost || 0) + (ride.tip || 0) + (ride.waitTimeAmount || 0)).toFixed(2);
    const msg = isCC
      ? `Card on file will be charged $${total}. Mark ride #${ride.rideId} as complete?`
      : `Mark ride #${ride.rideId} as complete?`;
    showAlert('Mark Complete', msg, [
      {
        text: 'Confirm', onPress: async () => {
          setDispatcherCompletingId(ride.rideId);
          let actionSucceeded = false;
          try {
            await ridesAPI.dispatcherCompleteRide(ride.rideId);
            actionSucceeded = true;
            showToast('Ride marked as complete', 'success');
          } catch (err) {
            showToast(`Failed: ${err.response?.data?.message || err.message}`, 'error');
          } finally {
            setDispatcherCompletingId(null);
          }
          if (actionSucceeded) {
            await fetchData().catch(e => console.warn('Failed to refresh after complete:', e));
          }
        }
      },
      { text: 'Cancel' }
    ]);
  };

  const handleDispatcherUncomplete = (ride) => {
    showAlert(
      'Mark Uncompleted',
      `This will revert ride #${ride.rideId} to PickedUp status. Payment status will NOT change.`,
      [
        {
          text: 'Confirm', onPress: async () => {
            setDispatcherUncompletingId(ride.rideId);
            let actionSucceeded = false;
            try {
              await ridesAPI.dispatcherUncompleteRide(ride.rideId);
              actionSucceeded = true;
              showToast('Ride reverted to PickedUp', 'success');
            } catch (err) {
              showToast(`Failed: ${err.response?.data?.message || err.message}`, 'error');
            } finally {
              setDispatcherUncompletingId(null);
            }
            if (actionSucceeded) {
              await fetchData().catch(e => console.warn('Failed to refresh after uncomplete:', e));
            }
          }
        },
        { text: 'Cancel' }
      ]
    );
  };

  // Mark a completed ride as reviewed
  const [reviewingRides, setReviewingRides] = useState({});
  const handleMarkAsReviewed = async (rideId) => {
    setReviewingRides(prev => ({ ...prev, [rideId]: true }));
    try {
      await dashboardAPI.markRideAsReviewed(rideId);
      showToast('Ride marked as reviewed', 'success');
      // Remove ride from list
      setData(prev => prev.filter(r => r.rideId !== rideId));
    } catch (error) {
      console.error('Error marking ride as reviewed:', error);
      showAlert('Error', 'Failed to mark ride as reviewed. Please try again.', [{ text: 'OK' }], 'error');
    } finally {
      setReviewingRides(prev => ({ ...prev, [rideId]: false }));
    }
  };

  const handleCancelCall = async (callId) => {
    showAlert(
      'Cancel Call',
      'Are you sure you want to cancel this call?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'CallCanceled',
                args: [callId],
                onRetrying: (attempt, max) => {
                  showToast(`Retrying... (${attempt}/${max})`, 'info');
                }
              });

              if (!result.success) {
                showAlert('Connection Error', `Failed to cancel call: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                return;
              }

              showToast('Call cancelled successfully', 'success');
              // Refresh the data
              window.location.reload();
            } catch (error) {
              console.error('Failed to cancel call:', error);
              showAlert('Error', `Failed to cancel call: ${error.message}`, [{ text: 'OK' }], 'error');
            }
          }
        }
      ],
      'warning'
    );
  };

  // Unassign a driver from a call (does NOT make it available to other drivers)
  const handleUnassignCall = async (callId) => {
    showAlert(
      'Unassign Driver',
      'Are you sure you want to remove the driver from this call? The call will become available again.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unassign',
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'UnassignCall',
                args: [callId],
                onRetrying: (attempt, max) => {
                  showToast(`Retrying... (${attempt}/${max})`, 'info');
                }
              });

              if (!result.success) {
                showAlert('Connection Error', `Failed to unassign driver: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                return;
              }

              showToast('Driver unassigned successfully', 'success');
              window.location.reload();
            } catch (error) {
              console.error('Failed to unassign driver:', error);
              showAlert('Error', `Failed to unassign driver: ${error.message}`, [{ text: 'OK' }], 'error');
            }
          }
        }
      ],
      'warning'
    );
  };

  // Open the assign/reassign menu for a ride
  const handleOpenAssignMenu = async (event, rideId) => {
    setAssignMenuAnchorEl(event.currentTarget);
    setAssignMenuRideId(rideId);

    // Load available drivers
    setLoadingDrivers(true);
    try {
      const drivers = await driversAPI.getAll();
      setAvailableDrivers(drivers || []);
    } catch (error) {
      console.error('Failed to load drivers:', error);
      showAlert('Error', 'Failed to load drivers list', [{ text: 'OK' }], 'error');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleCloseAssignMenu = () => {
    setAssignMenuAnchorEl(null);
    setAssignMenuRideId(null);
  };

  // Get ride info for the assign menu
  const getAssignMenuRide = () => {
    return data?.find(r => r.rideId === assignMenuRideId);
  };

  // Check if ride is currently assigned
  const isRideAssigned = (ride) => {
    return ride && ((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId));
  };

  // Confirm and assign/reassign a call to a specific driver
  const handleSelectDriver = (driver) => {
    if (!driver) return;

    const ride = getAssignMenuRide();
    const isAssigned = isRideAssigned(ride);
    const actionText = isAssigned ? 'reassign' : 'assign';

    handleCloseAssignMenu();

    showAlert(
      `Confirm ${isAssigned ? 'Reassignment' : 'Assignment'}`,
      `Are you sure you want to ${actionText} this call to ${driver.name} (#${driver.id})?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: isAssigned ? 'Reassign' : 'Assign',
          onPress: async () => {
            try {
              const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'DispatcherAssignCall',
                args: [assignMenuRideId, driver.id],
                onRetrying: (attempt, max) => {
                  showToast(`Retrying... (${attempt}/${max})`, 'info');
                }
              });

              if (!result.success) {
                showAlert('Connection Error', `Failed to assign call: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                return;
              }

              showToast('Call assigned successfully', 'success');
              window.location.reload();
            } catch (error) {
              console.error('Failed to assign call:', error);
              showAlert('Error', `Failed to assign call: ${error.message}`, [{ text: 'OK' }], 'error');
            }
          }
        }
      ],
      'warning'
    );
  };

  // Cancel recurring series with options
  const handleCancelRecurring = (rideId) => {
    showAlert(
      'Cancel Recurring Series',
      'Do you want to keep this ride or cancel it along with the series?',
      [
        { text: 'Go Back', style: 'cancel' },
        {
          text: 'Keep This Ride',
          onPress: async () => {
            try {
              await ridesAPI.cancelRecurringWithOption(rideId, true);
              setData(prevData =>
                prevData.map(ride =>
                  ride.rideId === rideId ? { ...ride, isRecurring: false } : ride
                )
              );
              showToast('Recurring series canceled. Current ride kept.', 'success');
            } catch (error) {
              console.error('Error canceling recurring ride:', error);
              showAlert('Error', 'Failed to cancel recurring ride. Please try again.', [{ text: 'OK' }], 'error');
            }
          }
        },
        {
          text: 'Cancel Full Series',
          style: 'destructive',
          onPress: async () => {
            try {
              await ridesAPI.cancelRecurringWithOption(rideId, false);
              setData(prevData =>
                prevData.map(ride =>
                  ride.rideId === rideId ? { ...ride, isRecurring: false, canceled: true } : ride
                )
              );
              showToast('Recurring series and current ride canceled.', 'success');
            } catch (error) {
              console.error('Error canceling recurring ride:', error);
              showAlert('Error', 'Failed to cancel recurring ride. Please try again.', [{ text: 'OK' }], 'error');
            }
          }
        }
      ],
      'warning'
    );
  };

  const handleResetPickupTime = async (callId) => {
    showAlert(
      'Reset Pickup Time',
      'Are you sure you want to reset the pickup time for this call? The driver will be notified.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'ResetPickupTime',
                args: [callId],
                onRetrying: (attempt, max) => {
                  showToast(`Retrying... (${attempt}/${max})`, 'info');
                }
              });

              if (!result.success) {
                showAlert('Connection Error', `Failed to reset pickup time: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                return;
              }

              showToast('Pickup time reset and driver notified', 'success');
              // Refresh the data
              window.location.reload();
            } catch (error) {
              console.error('Failed to reset pickup time:', error);
              showAlert('Error', `Failed to reset pickup time: ${error.message}`, [{ text: 'OK' }], 'error');
            }
          }
        }
      ],
      'warning'
    );
  };

  const handleMessageDriver = (driverId, driverName, ride = null) => {
    // If messaging from a ride, determine the correct driver ID
    // If ride.Reassigned is true, use ride.ReassignedToId, else use ride.AssignedToId
    let targetDriverId = driverId;

    if (ride) {
      targetDriverId = ride.reassigned ? ride.reassignedToId : ride.assignedToId;
    }

    // Mark messages from this driver as read
    if (onMarkMessagesRead && targetDriverId) {
      onMarkMessagesRead(targetDriverId);
    }

    // Use the openMessagingModal function passed from parent if available
    if (openMessagingModal) {
      openMessagingModal(targetDriverId);
    } else {
      // Fallback to old approach (shouldn't happen)
      setMessagingDriverId(targetDriverId);
      setMessagingDriverName(driverName || `Driver #${targetDriverId}`);
      setMessagingRideContext(ride ? {
        rideId: ride.rideId,
        customerName: ride.customerName,
        customerPhone: ride.customerPhoneNumber
      } : null);
      setShowMessaging(true);
    }
  };

  const handleMessagingClose = () => {
    setShowMessaging(false);
    setMessagingDriverId(null);
    setMessagingDriverName('');
    setMessagingRideContext(null);

    // Notify parent that modal is closed
    if (onMessagingModalClose) {
      onMessagingModalClose();
    }
  };

  const handleEditPrice = (rideId, currentPrice, currentDriverComp) => {
    setEditingPriceRideId(rideId);
    setEditedPrice(currentPrice.toString());
    setEditedDriverComp(currentDriverComp.toString());
  };

  const handleEditRide = (ride) => {
    setEditingRide(ride);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingRide(null);
  };

  const handleRideUpdated = async () => {
    // Refresh data after a ride is updated so the list reflects the latest values.
    // Always refetch (covering every metric type) and always replace the data,
    // otherwise the list keeps stale ride data and re-opening Edit shows old values.
    try {
      await fetchData();
    } catch (error) {
      console.error('Failed to refresh data after ride update:', error);
    }
  };

  // Paid status toggle handlers
  const handleOpenReceiptDialog = (rideId) => {
    setReceiptRideId(rideId);
    setReceiptEmail('');
    setReceiptDialogOpen(true);
  };

  const handleSendReceipt = async () => {
    setReceiptSending(true);
    try {
      const result = await ridesAPI.sendReceipt(receiptRideId, receiptEmail || null);
      showToast(`Receipt sent to: ${result.sentTo?.join(', ') || 'recipient'}`, 'success');
      setReceiptDialogOpen(false);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || err.message;
      showAlert('Failed to Send Receipt', typeof msg === 'string' ? msg : 'Please provide an email address.', [{ text: 'OK' }], 'error');
    } finally {
      setReceiptSending(false);
    }
  };

  const handleOpenPaidStatusModal = (ride) => {
    setPaidStatusRide(ride);
    setPaidStatusModalOpen(true);
    // Pre-select current payment type if already paid, otherwise default to cash
    setSelectedPaymentType(ride.paid ? (ride.paymentType || 'cash') : 'cash');
    setCollectedCardToken(null);
  };

  const handleClosePaidStatusModal = () => {
    setPaidStatusModalOpen(false);
    setPaidStatusRide(null);
    setSelectedPaymentType('');
    setCollectedCardToken(null);
  };

  // Handle a card being tokenized in the mark-paid modal (Card on File with no saved card yet)
  const handlePaidModalCardToken = async (nonce, cardholderName) => {
    try {
      const customerName = paidStatusRide?.customerName || cardholderName || 'Unknown';
      const result = await paymentAPI.saveCardOnFile(nonce, customerName);
      if (result.success) {
        setCollectedCardToken(result.cardId);
        showToast(`${result.cardBrand} ending in ${result.last4} ready to charge`, 'success');
      } else {
        showAlert('Error', `Failed to save card: ${result.message}`, [{ text: 'OK' }], 'error');
      }
    } catch (err) {
      console.error('SaveCardOnFile error:', err);
      showAlert('Error', 'Failed to process card. Please try again.', [{ text: 'OK' }], 'error');
    }
  };

  const handleConfirmPaidStatus = async () => {
    if (!paidStatusRide) return;

    const newPaidStatus = !paidStatusRide.paid;

    // If marking as paid, require payment type
    if (newPaidStatus && !selectedPaymentType) {
      showAlert('Please select a payment method', 'error');
      return;
    }

    // Card on file requires a card to charge (existing token or a newly-collected one)
    const isDispatcherCC = newPaidStatus && selectedPaymentType === 'dispatcherCC';
    const cardToken = collectedCardToken || paidStatusRide.paymentTokenId || null;
    if (isDispatcherCC && !cardToken) {
      showToast('Please enter a credit card to charge', 'error');
      return;
    }

    try {
      setSettingPaidStatus(true);
      await ridesAPI.setPaidStatus(
        paidStatusRide.rideId,
        newPaidStatus,
        newPaidStatus ? selectedPaymentType : null,
        isDispatcherCC ? cardToken : null
      );

      // Notify driver via SignalR so their app updates in real-time
      const driverId = paidStatusRide.reassigned ? paidStatusRide.reassignedToId : paidStatusRide.assignedToId;
      if (driverId && signalRConnection) {
        try {
          const updatePayload = {
            rideId: paidStatusRide.rideId,
            paid: newPaidStatus,
            paymentType: newPaidStatus ? selectedPaymentType : null,
            changes: [newPaidStatus ? `Payment status changed to Paid (${selectedPaymentType})` : 'Payment status changed to Unpaid']
          };

          await safeInvoke({
            connection: signalRConnection,
            connectFn: connectSignalR,
            methodName: 'RideUpdated',
            args: [updatePayload]
          });
          console.log('✅ Driver notified of payment status change via SignalR');
        } catch (signalRError) {
          console.warn('Could not notify driver via SignalR:', signalRError);
          // Don't fail the operation if SignalR fails - the DB is already updated
        }
      }

      showToast(
        newPaidStatus
          ? (isDispatcherCC ? 'Card charged \u2014 ride marked as paid' : 'Ride marked as paid')
          : 'Ride marked as unpaid',
        'success'
      );
      handleClosePaidStatusModal();
      // Refresh the data
      await handleRideUpdated();
    } catch (err) {
      console.error('Failed to update paid status:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to update paid status';
      showAlert('Payment Error', msg, [{ text: 'OK' }], 'error');
    } finally {
      setSettingPaidStatus(false);
    }
  };

  const handleCancelEditPrice = () => {
    setEditingPriceRideId(null);
    setEditedPrice('');
    setEditedDriverComp('');
  };

  const handleSavePrice = async (rideId) => {
    const newPrice = parseFloat(editedPrice);
    const newDriverComp = parseFloat(editedDriverComp);

    if (isNaN(newPrice) || newPrice < 0) {
      showAlert('Error', 'Please enter a valid price', [{ text: 'OK' }], 'error');
      return;
    }

    if (isNaN(newDriverComp) || newDriverComp < 0) {
      showAlert('Error', 'Please enter a valid driver compensation', [{ text: 'OK' }], 'error');
      return;
    }

    try {
      await ridesAPI.updatePrice(rideId, newPrice, newDriverComp);

      // Update local data
      setData(prevData =>
        prevData.map(ride =>
          ride.rideId === rideId ? { ...ride, cost: newPrice, driversCompensation: newDriverComp } : ride
        )
      );

      showToast('Price updated successfully', 'success');
      setEditingPriceRideId(null);
      setEditedPrice('');
      setEditedDriverComp('');
    } catch (error) {
      console.error('Error updating price:', error);
      showAlert('Error', 'Failed to update price. Please try again.', [{ text: 'OK' }], 'error');
    }
  };

  const getTitle = () => {
    switch (metricType) {
      case 'assignedRides':
        return 'Assigned Rides';
      case 'ridesInProgress':
        return 'Rides in Progress';
      case 'openRides':
        return 'Open Rides';
      case 'completedUnreviewedRides':
        return 'Completed Rides - Pending Review';
      case 'recurringRidesThisWeek':
        return 'Recurring Rides This Week';
      case 'todaysRides':
        return 'Today\'s Rides';
      case 'futureRides':
        return 'Future Rides';
      case 'activeDrivers':
        return 'Active Drivers';
      case 'offlineDrivers':
        return 'Offline Drivers';
      case 'driversOnJob':
        return 'Drivers On Job';
      case 'unsettledDrivers':
        return 'Unsettled Drivers';
      default:
        return 'List View';
    }
  };

  const isRideList = metricType?.includes('Rides') || metricType?.includes('rides');
  const isDriverList = (metricType?.includes('Drivers') || metricType?.includes('drivers')) && metricType !== 'unsettledDrivers';
  const isUnsettledDriversList = metricType === 'unsettledDrivers';

  // Filter data based on search query
  const getFilteredData = () => {
    if (!searchQuery.trim() || !Array.isArray(data)) {
      return data;
    }

    const query = searchQuery.toLowerCase().trim();

    if (isRideList) {
      return data.filter(ride => {
        const searchableFields = [
          ride.rideId?.toString(),
          ride.customerName,
          ride.customerPhoneNumber,
          ride.route?.pickup,
          ride.route?.dropOff,
          ride.assignedTo?.name.toString(),
          ride.notes,
          formatDateTime(ride.callTime),
          formatDateTime(ride.scheduledFor)
        ];
        return searchableFields.some(field =>
          field && field.toLowerCase().includes(query)
        );
      });
    }

    if (isDriverList) {
      return data.filter(driver => {
        const searchableFields = [
          driver.id?.toString(),
          driver.name,
          driver.phoneNumber,
          driver.email,
        ];
        return searchableFields.some(field =>
          field && field.toLowerCase().includes(query)
        );
      });
    }

    if (isUnsettledDriversList) {
      return data.filter(driver => {
        const searchableFields = [
          driver.id?.toString(),
          driver.name,
          driver.phoneNumber,
          driver.email,
        ];
        return searchableFields.some(field =>
          field && field.toLowerCase().includes(query)
        );
      });
    }

    return data;
  };

  const filteredData = getFilteredData();

  // Group rides by week for collapsible display
  const groupRidesByWeek = (rides) => {
    if (!Array.isArray(rides) || rides.length === 0) return [];

    const weekGroups = {};
    rides.forEach(ride => {
      const dateToUse = ride.scheduledFor || ride.callTime;
      if (!dateToUse) return;

      const rideDate = parseUtcDate(dateToUse);
      const weekStart = getWeekStart(rideDate);
      const weekKey = weekStart.toISOString();

      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = {
          weekStart,
          label: getWeekLabel(weekStart),
          rides: []
        };
      }
      weekGroups[weekKey].rides.push(ride);
    });

    // Sort weeks oldest first, then sort rides within each week oldest first
    return Object.values(weekGroups)
      .sort((a, b) => a.weekStart - b.weekStart)
      .map(group => ({
        ...group,
        rides: group.rides.sort((a, b) => {
          const dateA = parseUtcDate(a.scheduledFor || a.callTime);
          const dateB = parseUtcDate(b.scheduledFor || b.callTime);
          return dateA - dateB; // Oldest first
        })
      }));
  };

  const toggleWeek = (weekKey, isFirstWeek = false) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      const collapsedKey = '_collapsed_' + weekKey;

      if (isFirstWeek) {
        // For first week: toggle between expanded (default) and collapsed
        if (newSet.has(collapsedKey)) {
          newSet.delete(collapsedKey); // Re-expand
        } else {
          newSet.add(collapsedKey); // Collapse
        }
      } else {
        // For other weeks: toggle between collapsed (default) and expanded
        if (newSet.has(weekKey)) {
          newSet.delete(weekKey);
        } else {
          newSet.add(weekKey);
        }
      }
      return newSet;
    });
  };

  const renderRideItem = (ride) => {
    const status = getRideStatus(ride);
    return (
      <Card
        key={ride.rideId}
        sx={{
          mb: 2,
          backgroundColor: ride.canceled ? '#ffebee' : 'inherit',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        {ride.canceled && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-15deg)',
              zIndex: 10,
              pointerEvents: 'none'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: '#d32f2f',
                fontWeight: 'bold',
                opacity: 0.8,
                textTransform: 'uppercase',
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                letterSpacing: '0.2em',
                fontStyle: 'italic'
              }}
            >
              Canceled
            </Typography>
          </Box>
        )}
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">
              Ride Id #{ride.rideId}
            </Typography>
            <Chip
              label={status}
              color={getRideStatusColor(status)}
              size="small"
              sx={getRideStatusStyle(status)}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3} alignItems="flex-start">
            {/* Customer/Phone section */}
            <Box>
              {ride.customerName && <Typography variant="body2" color="text.secondary">
                👨‍👩‍👧‍👦 <strong>Customer:</strong> {ride.customerName}
              </Typography>}
              {ride.customerPhoneNumber && <Typography variant="body2" color="text.secondary">
                ☎️ <strong>Phone #:</strong> {ride.customerPhoneNumber}
              </Typography>}
            </Box>

            {/* Driver section */}
            {((!ride.reassigned && ride.assignedToId) || ride.reassignedToId) && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  🚘 <strong>Driver:</strong> #{ride.reassigned ? ride.reassignedToId : ride.assignedToId} - {ride.reassigned ? ride.reassignedTo?.name : ride.assignedTo?.name}
                </Typography>
                {(ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber) && (
                  <Typography variant="body2" color="text.secondary">
                    ☎️ <strong>Driver Phone #:</strong> {ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber}
                  </Typography>
                )}
              </Box>
            )}

            {/* Cost and Driver's Compensation - Admin Only */}
            {isAdmin && (
              <Box>
                {editingPriceRideId === ride.rideId ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type="number"
                        value={editedPrice}
                        onChange={(e) => setEditedPrice(e.target.value)}
                        label="Cost"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ width: '150px' }}
                        autoFocus
                      />
                      <TextField
                        size="small"
                        type="number"
                        value={editedDriverComp}
                        onChange={(e) => setEditedDriverComp(e.target.value)}
                        label="Driver Comp"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ width: '150px' }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSavePrice(ride.rideId)}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelEditPrice}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        💵 <strong>Cost:</strong> ${ride.cost || 0}
                      </Typography>
                      {!ride.dropOffTime && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditPrice(ride.rideId, ride.cost || 0, ride.driversCompensation || 0)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      💰 <strong>Driver Comp:</strong> ${ride.driversCompensation || 0}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}


            {/* Times section */}
            <Box>
              {ride.callTime && (
                <Typography variant="body2" color="text.secondary">
                  📞 <strong>Call Time:</strong> {formatDateTime(ride.callTime)}
                </Typography>
              )}
              {ride.scheduledFor && (
                <Typography variant="body2" color="text.secondary">
                  🗓️ <strong>Scheduled:</strong> {formatDateTime(ride.scheduledFor)}
                </Typography>
              )}
            </Box>

            {/* Pickup/Dropoff status */}
            <Box>
              <Typography variant="body2" color={ride.pickupTime ? "success.main" : "error.main"}>
                <strong>Picked Up?</strong> {ride.pickupTime ? '✅' : '❌'}
              </Typography>

              <Typography variant="body2" color={ride.dropOffTime ? "success.main" : "error.main"}>
                <strong>Dropped Off?</strong> {ride.dropOffTime ? '✅' : '❌'}
              </Typography>
            </Box>

            {/* Canceled indicator */}
            {ride.canceled && (
              <Box>
                <Typography variant="body2" color="error.main">
                  ❌ <strong>Canceled</strong>
                </Typography>
              </Box>
            )}

            {/* Recurring indicator */}
            {ride.isRecurring && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <RepeatIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="primary.main">
                  <strong>Recurring Ride</strong>
                </Typography>
              </Box>
            )}

            {/* Round Trip indicator */}
            {ride.route?.roundTrip && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" sx={{ color: '#856404' }}>
                  🔄 <strong>Round Trip</strong>
                </Typography>
              </Box>
            )}
          </Box>

        </CardContent>

        <CardActions sx={{ flexWrap: 'wrap', gap: 1, p: 2, pt: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleItemClick('ride', ride.rideId)}
          >
            View Details
          </Button>

          {ride.completed && (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<ReceiptIcon />}
              onClick={() => handleOpenReceiptDialog(ride.rideId)}
            >
              Send Receipt
            </Button>
          )}

          {/* Edit button for rides that are not canceled or completed */}
          {(!ride.canceled && !ride.dropOffTime) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditRide(ride)}
            >
              Edit
            </Button>
          )}

          {/* Show Message button for all non-canceled rides that aren't completed (dropOffTime is null) */}
          {/* Use reassigned driver if ride was reassigned */}
          {(!ride.canceled && !ride.dropOffTime && ((!ride.reassigned && ride.assignedToId) || ride.reassignedToId)) && (
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={<MessageIcon />}
              onClick={() => handleMessageDriver(null, null, ride)}
            >
              Message Driver
            </Button>
          )}
          {/* Cancel button - only show if not picked up yet */}
          {(!ride.canceled && !ride.pickupTime) && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleCancelCall(ride.rideId)}
            >
              Cancel
            </Button>
          )}

          {/* Cancel Recurring button - only show for recurring rides */}
          {ride.isRecurring && !ride.canceled && !ride.dropOffTime && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<RepeatIcon />}
              onClick={() => handleCancelRecurring(ride.rideId)}
            >
              Cancel Recurring Series
            </Button>
          )}

          {/* Unassign button - removes driver without making available */}
          {(!ride.canceled && ((!ride.reassigned && ride.assignedToId) ||
            (ride.reassigned && ride.reassignedToId)) && !ride.pickupTime) && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={<UnassignIcon />}
                onClick={() => handleUnassignCall(ride.rideId)}
              >
                Unassign
              </Button>
            )}

          {/* Assign/Reassign dropdown - assigns to specific driver */}
          {(!ride.canceled && !ride.pickupTime) && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId)) ? <ReassignIcon /> : <AssignIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => handleOpenAssignMenu(e, ride.rideId)}
            >
              {((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId)) ? 'Reassign' : 'Assign'}
            </Button>
          )}

          {(ride.pickupTime && !ride.dropOffTime) && (

            <Button
              variant="outlined"
              size="small"
              color='primary'
              startIcon={<ResetIcon />}
              onClick={() => handleResetPickupTime(ride.rideId)}
            >
              Reset Pickup Time
            </Button>
          )}

          {/* Mark Paid/Unpaid button - show for non-canceled rides */}
          {!ride.canceled && (
            <Button
              variant="contained"
              size="small"
              color={ride.paid ? "error" : "success"}
              onClick={() => handleOpenPaidStatusModal(ride)}
            >
              {ride.paid ? 'Mark Unpaid' : 'Mark Paid'}
            </Button>
          )}

          {/* Mark as Reviewed button - only for completed unreviewed rides */}
          {metricType === 'completedUnreviewedRides' && (
            <Button
              variant="contained"
              size="small"
              color="info"
              disabled={reviewingRides[ride.rideId]}
              startIcon={reviewingRides[ride.rideId] ? <CircularProgress size={16} /> : <CheckIcon />}
              onClick={() => handleMarkAsReviewed(ride.rideId)}
            >
              {reviewingRides[ride.rideId] ? 'Reviewing...' : 'Mark as Reviewed'}
            </Button>
          )}

          {/* Dispatcher complete / uncomplete */}
          {!ride.canceled && (() => {
            const s = (ride.status || '').toLowerCase();
            const canComplete = s === 'pickedup' || s === 'droppedoff' || s.startsWith('atstop');
            const canUncomplete = s === 'completed';
            return (
              <>
                {canComplete && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    disabled={dispatcherCompletingId === ride.rideId}
                    startIcon={dispatcherCompletingId === ride.rideId ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={() => handleDispatcherComplete(ride)}
                  >
                    {dispatcherCompletingId === ride.rideId ? 'Completing...' : '✅ Mark Complete'}
                  </Button>
                )}
                {canUncomplete && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    disabled={dispatcherUncompletingId === ride.rideId}
                    startIcon={dispatcherUncompletingId === ride.rideId ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={() => handleDispatcherUncomplete(ride)}
                  >
                    {dispatcherUncompletingId === ride.rideId ? 'Reverting...' : '↩️ Mark Uncompleted'}
                  </Button>
                )}
              </>
            );
          })()}

        </CardActions>
      </Card >
    )
  };

  const renderDriverItem = (driver) => {
    const status = driverStatuses[driver.id] || 'loading';
    const unreadCount = driversWithUnread.get(driver.id) || 0;

    return (
      <Card key={driver.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" color="primary">
                Driver Id #{driver.id}
              </Typography>
              {!!unreadCount && (
                <Badge color="error" variant="dot">
                  <UnreadIcon color="error" fontSize="small" />
                </Badge>
              )}
            </Box>
            <Chip
              label={status}
              color={getDriverStatusColor(status)}
              size="small"
              sx={getRideStatusStyle(status)}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                🙎‍♂️ <strong>Name:</strong> {driver.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ☎️ <strong>Phone #:</strong> {driver.phoneNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                📧 <strong>Email:</strong> {driver.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                🚗 Has {driver.cars?.length ?? 0} Car{driver.cars?.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ gap: 1, p: 2, pt: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleItemClick('driver', driver.id)}
          >
            View Details
          </Button>

          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<MessageIcon />}
              onClick={() => handleMessageDriver(driver.id, driver.name)}
            >
              Message Driver
            </Button>
          </Badge>
        </CardActions>
      </Card>
    )
  };

  // Render unsettled driver item - similar to renderDriverItem but with unsettled rides section
  const renderUnsettledDriverItem = (driver) => {
    const status = driverStatuses[driver.id] || 'loading';
    const unreadCount = driversWithUnread.get(driver.id) || 0;
    const unsettledCount = unsettledRidesCounts[driver.id];
    const isExpanded = expandedDrivers.has(driver.id);
    const rides = unsettledRidesMap[driver.id] || [];
    const isLoadingRides = loadingUnsettledRides[driver.id];
    const isSettling = settlingDrivers[driver.id];

    // Calculate total amounts
    let totalOwedToDriver = 0; // Company owes driver (CC payments)
    let totalOwedByDriver = 0; // Driver owes company (Cash/Other)
    rides.forEach(ride => {

      if (ride.paymentType === 'cash' || ride.paymentType === 'zelle') {
        totalOwedByDriver += (ride.cost - ride.driversCompensation);
      } else {
        totalOwedToDriver += ride.driversCompensation;
      }
    });

    return (
      <Card key={driver.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" color="primary">
                Driver Id #{driver.id}
              </Typography>
              {unsettledCount !== undefined && (
                <Chip
                  size="small"
                  label={`${unsettledCount} unsettled`}
                  color={`${unsettledCount >= 50 ? 'error' : unsettledCount >= 25 ? 'warning' : 'success'}`}
                  icon={<SettlementIcon />}
                />
              )}
              {!!unreadCount && (
                <Badge color="error" variant="dot">
                  <UnreadIcon color="error" fontSize="small" />
                </Badge>
              )}
            </Box>
            <Chip
              label={status}
              color={getDriverStatusColor(status)}
              size="small"
              sx={getRideStatusStyle(status)}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                🙎‍♂️ <strong>Name:</strong> {driver.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ☎️ <strong>Phone #:</strong> {driver.phoneNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                📧 <strong>Email:</strong> {driver.email}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ gap: 1, p: 2, pt: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              size="small"
              onClick={() => handleItemClick('driver', driver.id)}
            >
              View Details
            </Button>

            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<MessageIcon />}
                onClick={() => handleMessageDriver(driver.id, driver.name)}
              >
                Message Driver
              </Button>
            </Badge>

            <Button
              variant="outlined"
              size="small"
              onClick={() => toggleDriverExpand(driver.id)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {isExpanded ? 'Hide Unsettled Rides' : 'Show Unsettled Rides'}
            </Button>
          </Box>

          {/* Collapsible unsettled rides section */}
          <Collapse in={isExpanded} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {isLoadingRides ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : rides.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No unsettled rides found.
              </Typography>
            ) : (
              <>
                {/* Summary */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    💰 Settlement Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="success.main">
                        <strong>Company owes driver:</strong> ${totalOwedToDriver.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="error.main">
                        <strong>Driver owes company:</strong> ${totalOwedByDriver.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="body1" fontWeight="bold">
                        Net: {totalOwedToDriver > totalOwedByDriver ? (
                          <span style={{ color: 'green' }}>Pay driver ${(totalOwedToDriver - totalOwedByDriver).toFixed(2)}</span>
                        ) : totalOwedByDriver > totalOwedToDriver ? (
                          <span style={{ color: 'red' }}>Collect ${(totalOwedByDriver - totalOwedToDriver).toFixed(2)} from driver</span>
                        ) : (
                          <span>Even - $0.00</span>
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      {/* Settle All button */}
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => handleSettleUpDriver(driver.id)}
                        disabled={isSettling}
                        startIcon={isSettling ? <CircularProgress size={20} color="inherit" /> : <SettlementIcon />}
                      >
                        {isSettling ? 'Settling Up...' : `Settle Up All (${rides.length}) rides and Send Invoice`}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {/* Rides table */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Ride ID</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Payment</strong></TableCell>
                        <TableCell align="right"><strong>Cost</strong></TableCell>
                        <TableCell align="right"><strong>Driver Comp</strong></TableCell>
                        <TableCell align="right"><strong>Unsettled Amt</strong></TableCell>
                        {/* <TableCell align="center"><strong>Action</strong></TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rides.map((ride) => {
                        const amount = calculateUnsettledAmount(ride);
                        const isSettlingRide = settlingRides[ride.rideId];
                        const isPrepaid = ride.paid || ride.paymentTokenId || ride.paymentType === 'dispatcherCC';
                        // Get display-friendly payment type label
                        const getPaymentLabel = () => {
                          if (ride.paymentType === 'zelle') return 'Zelle';
                          if (ride.paymentType === 'dispatcherCC' || ride.paymentTokenId) return 'Card on File';
                          if (ride.paymentType === 'driverCC') return 'Driver CC';
                          if (ride.paymentType === 'cash') return 'Cash';
                          if (ride.paid) return 'Paid';
                          return 'Cash';
                        };
                        const getPaymentColor = () => {
                          if (ride.paid || ride.paymentType === 'zelle') return 'success';
                          if (ride.paymentTokenId || ride.paymentType === 'dispatcherCC') return 'info';
                          return 'warning';
                        };
                        return (
                          <TableRow key={ride.rideId} hover>
                            <TableCell>{ride.rideId}</TableCell>
                            <TableCell>
                              {ride.scheduledFor ? formatDateTime(ride.scheduledFor) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getPaymentLabel()}
                                color={getPaymentColor()}
                              />
                            </TableCell>
                            <TableCell align="right">${(ride.cost || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${(ride.driversCompensation || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={isPrepaid ? 'success.main' : 'error.main'}
                              >
                                ${isPrepaid ? (
                                  ride.driversCompensation.toFixed(2)) : (
                                  (ride.cost - ride.driversCompensation).toFixed(2))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

              </>
            )}
          </Collapse>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBackToDashboard}
            variant="outlined"
          >
            Back to Dashboard
          </Button>
          <Typography variant="h5" component="h2">
            {getTitle()}
          </Typography>
          <Chip
            label={`${Array.isArray(filteredData) ? filteredData.length : 0} of ${Array.isArray(data) ? data.length : 0} items`}
            color="primary"
          />
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder={isRideList ? "Search by ride ID, customer, phone, address..." : "Search by driver ID, name, phone, email..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ mt: 1 }}
        />
      </Paper>

      <Box>
        {loading ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Paper>
        ) : !Array.isArray(filteredData) || filteredData.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? `No results found for "${searchQuery}"` : `No ${getTitle().toLowerCase()} found.`}
            </Typography>
          </Paper>
        ) : (
          <Box>
            {isRideList && Array.isArray(filteredData) && (() => {
              const groupedWeeks = groupRidesByWeek(filteredData);
              return groupedWeeks.map((weekGroup, index) => {
                const weekKey = weekGroup.weekStart.toISOString();
                // First week is auto-expanded by default
                const isExpanded = expandedWeeks.has(weekKey) || (index === 0 && !expandedWeeks.has('_collapsed_' + weekKey));
                return (
                  <Box key={weekKey} sx={{ mb: 2 }}>
                    <Paper
                      onClick={() => toggleWeek(weekKey, index === 0)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#f5f5f5',
                        '&:hover': { backgroundColor: '#e0e0e0' }
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {weekGroup.label} ({weekGroup.rides.length} ride{weekGroup.rides.length !== 1 ? 's' : ''})
                      </Typography>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Paper>
                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 1 }}>
                        {weekGroup.rides.map(renderRideItem)}
                      </Box>
                    </Collapse>
                  </Box>
                );
              });
            })()}
            {isDriverList && Array.isArray(filteredData) && filteredData.map(renderDriverItem)}
            {isUnsettledDriversList && Array.isArray(filteredData) && filteredData.map(renderUnsettledDriverItem)}
          </Box>
        )}
      </Box>

      {/* Driver Messaging Modal */}
      <DriverMessagingModal
        isOpen={showMessaging || !!openMessagingDriverId}
        onClose={handleMessagingClose}
        driverId={openMessagingDriverId || messagingDriverId}
        driverName={messagingDriverName}
        rideContext={messagingRideContext}
        onNavigateToRideHistory={onNavigateToRideHistory}
      />

      {/* Edit Ride Modal */}
      <EditRideModal
        open={showEditModal}
        onClose={handleEditModalClose}
        ride={editingRide}
        onUpdate={handleRideUpdated}
      />

      {/* Assign/Reassign Driver Popover */}
      <Popover
        open={Boolean(assignMenuAnchorEl)}
        anchorEl={assignMenuAnchorEl}
        onClose={handleCloseAssignMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {isRideAssigned(getAssignMenuRide()) ? 'Reassign to Driver' : 'Assign to Driver'}
          </Typography>
          {loadingDrivers ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={2}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">Loading drivers...</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={availableDrivers}
              getOptionLabel={(option) => `#${option.id} - ${option.name}`}
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search drivers..."
                  size="small"
                  autoFocus
                />
              )}
              onChange={(_, value) => handleSelectDriver(value)}
              noOptionsText="No drivers found"
              sx={{ minWidth: 250 }}
            />
          )}
        </Box>
      </Popover>

      {/* Paid Status Toggle Modal */}
      <Dialog open={paidStatusModalOpen} onClose={handleClosePaidStatusModal}>
        <DialogTitle>
          {paidStatusRide?.paid ? 'Mark Ride as Unpaid' : 'Mark Ride as Paid'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {paidStatusRide?.paid
              ? `Are you sure you want to mark ride #${paidStatusRide?.rideId} as unpaid?`
              : `Select the payment method for ride #${paidStatusRide?.rideId}:`
            }
          </DialogContentText>
          {!paidStatusRide?.paid && (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={selectedPaymentType}
                onChange={(e) => setSelectedPaymentType(e.target.value)}
                label="Payment Method"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="zelle">Zelle</MenuItem>
                <MenuItem value="dispatcherCC">Card on File</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          )}
          {!paidStatusRide?.paid && selectedPaymentType === 'dispatcherCC' && (() => {
            const total = ((paidStatusRide?.cost || 0) + (paidStatusRide?.tip || 0) + (paidStatusRide?.waitTimeAmount || 0));
            const hasCard = !!(collectedCardToken || paidStatusRide?.paymentTokenId);
            return (
              <Box sx={{ mt: 2 }}>
                {hasCard ? (
                  <DialogContentText sx={{ color: 'warning.main' }}>
                    The card on file will be charged <strong>${total.toFixed(2)}</strong> (plus 4% CC fee) when you confirm.
                  </DialogContentText>
                ) : (
                  <>
                    <DialogContentText sx={{ mb: 1 }}>
                      No card on file. Enter a credit card to charge <strong>${total.toFixed(2)}</strong> (plus 4% CC fee).
                    </DialogContentText>
                    <PaymentForm
                      onTokenGenerated={handlePaidModalCardToken}
                      onTokenCleared={() => setCollectedCardToken(null)}
                      onError={(err) => showAlert('Error', `Failed to process card: ${err}`, [{ text: 'OK' }], 'error')}
                    />
                  </>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaidStatusModal} disabled={settingPaidStatus}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPaidStatus}
            variant="contained"
            color={paidStatusRide?.paid ? "warning" : "success"}
            disabled={
              settingPaidStatus
              || (!paidStatusRide?.paid && !selectedPaymentType)
              || (!paidStatusRide?.paid && selectedPaymentType === 'dispatcherCC'
                && !(collectedCardToken || paidStatusRide?.paymentTokenId))
            }
          >
            {settingPaidStatus ? <CircularProgress size={20} /> : (paidStatusRide?.paid ? 'Mark Unpaid' : (selectedPaymentType === 'dispatcherCC' ? 'Charge & Mark Paid' : 'Mark Paid'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Receipt</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter an email address to send the receipt to. If the ride has a linked account with an email, it will also receive a copy.
            Leave blank to send only to the account email (or the company default).
          </DialogContentText>
          <TextField
            fullWidth
            label="Email Address (optional)"
            type="email"
            value={receiptEmail}
            onChange={e => setReceiptEmail(e.target.value)}
            placeholder="customer@example.com"
            helperText="Optional — adds this address as an additional recipient"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialogOpen(false)} disabled={receiptSending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendReceipt}
            disabled={receiptSending}
            startIcon={receiptSending ? <CircularProgress size={16} /> : <ReceiptIcon />}
          >
            Send Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MetricListView;
